import * as Tone from 'tone';

/**
 * Creates a multi-voice drum kit that maps incoming pitched notes
 * to different drum sounds (kick, snare, closed hat, open hat, clap, tom, rim, shaker).
 *
 * Returns an object with the same `triggerAttackRelease(note, duration, time)` API
 * as a Tone.js synth, so it's a drop-in replacement for MembraneSynth.
 */
export function createDrumKit(dest, volumeDb = -14) {
  // ── Kick ──
  const kick = new Tone.MembraneSynth({
    pitchDecay: 0.08,
    octaves: 6,
    envelope: { attack: 0.001, decay: 0.25, sustain: 0.0, release: 0.2 },
    volume: volumeDb + 2,
  }).connect(dest);

  // ── Snare body + noise ──
  const snareBody = new Tone.MembraneSynth({
    pitchDecay: 0.01,
    octaves: 4,
    envelope: { attack: 0.001, decay: 0.12, sustain: 0.0, release: 0.08 },
    volume: volumeDb - 2,
  }).connect(dest);
  const snareNoise = new Tone.NoiseSynth({
    noise: { type: 'white' },
    envelope: { attack: 0.001, decay: 0.13, sustain: 0.0, release: 0.06 },
    volume: volumeDb - 4,
  }).connect(dest);

  // ── Closed hi-hat ──
  const hihatClosed = new Tone.MetalSynth({
    frequency: 400,
    harmonicity: 5.1,
    modulationIndex: 32,
    resonance: 4000,
    envelope: { attack: 0.001, decay: 0.06, release: 0.01 },
    volume: volumeDb - 6,
  }).connect(dest);

  // ── Open hi-hat ──
  const hihatOpen = new Tone.MetalSynth({
    frequency: 400,
    harmonicity: 5.1,
    modulationIndex: 32,
    resonance: 4000,
    envelope: { attack: 0.001, decay: 0.25, release: 0.15 },
    volume: volumeDb - 8,
  }).connect(dest);

  // ── Clap ──
  const clap = new Tone.NoiseSynth({
    noise: { type: 'white' },
    envelope: { attack: 0.002, decay: 0.12, sustain: 0.0, release: 0.08 },
    volume: volumeDb - 4,
  }).connect(dest);

  // ── Tom (mid) ──
  const tom = new Tone.MembraneSynth({
    pitchDecay: 0.05,
    octaves: 4,
    envelope: { attack: 0.001, decay: 0.18, sustain: 0.0, release: 0.1 },
    volume: volumeDb - 2,
  }).connect(dest);

  // ── Rimshot ──
  const rim = new Tone.MembraneSynth({
    pitchDecay: 0.005,
    octaves: 2,
    envelope: { attack: 0.001, decay: 0.04, sustain: 0.0, release: 0.03 },
    volume: volumeDb - 3,
  }).connect(dest);

  // ── Shaker ──
  const shaker = new Tone.NoiseSynth({
    noise: { type: 'pink' },
    envelope: { attack: 0.005, decay: 0.04, sustain: 0.0, release: 0.02 },
    volume: volumeDb - 10,
  }).connect(dest);

  // Map note names to voices. The grid sends scale notes;
  // we use the MIDI-ish pitch to pick a drum voice.
  // Lower rows = lower pitch = kick; higher rows = higher pitch = hats.
  function noteToMidi(note) {
    const match = note.match(/^([A-G]#?)(\d+)$/);
    if (!match) return 60;
    const names = ['C','C#','D','D#','E','F','F#','G','G#','A','A#','B'];
    return names.indexOf(match[1]) + (parseInt(match[2]) + 1) * 12;
  }

  const voices = [kick, snareBody, tom, rim, clap, hihatClosed, hihatOpen, shaker];
  const allSynths = [kick, snareBody, snareNoise, hihatClosed, hihatOpen, clap, tom, rim, shaker];

  return {
    triggerAttackRelease(note, duration, time, velocity = 1) {
      const midi = noteToMidi(note);
      // Partition the MIDI range into 8 drum voices
      // Typical range: C2(36)–C5(72). Bucket by pitch.
      let idx;
      if (midi <= 40)      idx = 0; // kick
      else if (midi <= 48) idx = 1; // snare
      else if (midi <= 52) idx = 2; // tom
      else if (midi <= 56) idx = 3; // rim
      else if (midi <= 60) idx = 4; // clap
      else if (midi <= 67) idx = 5; // closed hat
      else if (midi <= 74) idx = 6; // open hat
      else                 idx = 7; // shaker

      try {
        const voice = voices[idx];
        if (idx === 0) {
          // Kick — pitched
          voice.triggerAttackRelease('C1', duration, time, velocity);
        } else if (idx === 1) {
          // Snare — body + noise layer
          snareBody.triggerAttackRelease('E2', duration, time, velocity);
          snareNoise.triggerAttackRelease(duration, time, velocity);
        } else if (idx === 2) {
          // Tom
          voice.triggerAttackRelease('A1', duration, time, velocity);
        } else if (idx === 3) {
          // Rim
          voice.triggerAttackRelease('C3', '32n', time, velocity);
        } else if (idx === 4 || idx === 7) {
          // Clap / Shaker (NoiseSynth — no pitch)
          voice.triggerAttackRelease(duration, time, velocity);
        } else {
          // Hi-hats (MetalSynth — no pitch arg)
          voice.triggerAttack(time, velocity);
        }
      } catch (_) {}
    },

    dispose() {
      for (const s of allSynths) {
        try { s.dispose(); } catch (_) {}
      }
    },
  };
}
